import * as React from "react";
import { cn } from "@/lib/utils";
import { useEffectEvent } from "@/hooks/use-effect-event";

export interface TextareaProps extends React.ComponentProps<"textarea"> {
  warning?: boolean;
}

function Textarea({
  className,
  required,
  value,
  defaultValue,
  warning,
  ref,
  ...props
}: TextareaProps) {
  const [isEmpty, setIsEmpty] = React.useState(() => {
    const initialValue = value !== undefined ? value : defaultValue || "";
    return !initialValue || initialValue.toString().trim() === "";
  });

  const isControlled = value !== undefined;
  const shouldHighlight = required && isEmpty;

  // Event para sincronizar isEmpty sin causar re-renders innecesarios
  const syncIsEmpty = useEffectEvent(() => {
    if (isControlled && value !== undefined) {
      const newIsEmpty = !value || value.toString().trim() === "";
      setIsEmpty(newIsEmpty);
    }
  });

  // Sincronizar cuando cambia value (modo controlado)
  React.useEffect(() => {
    syncIsEmpty();
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    setIsEmpty(!target.value || target.value.trim() === "");
    props.onInput?.(e);
  };

  const setTextareaRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  return (
    <textarea
      ref={setTextareaRef}
      data-slot="textarea"
      required={required}
      {...(isControlled ? { value } : { defaultValue })}
      className={cn(
        "border-ring/30  bg-background placeholder:text-muted-foreground placeholder:text-sm aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border  px-3 py-2 text-base shadow-[0_2px_6px_rgba(0,0,0,0.06)] hover:shadow-[0_3px_10px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_6px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_3px_10px_rgba(0,0,0,0.12)] transition-all outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm opacity-70 focus-visible:opacity-100",
        "focus-visible:ring-ring/30 focus-visible:ring-[1px]",
        shouldHighlight && "border-foreground/40 ring-foreground/40",
        warning &&
          "border-yellow-500 ring-yellow-500/20 ring-1 dark:border-yellow-600 dark:ring-yellow-600/20",
        className,
      )}
      onInput={handleInput}
      {...props}
    />
  );
}

export { Textarea };